import { API_BASE_URL } from './constants';

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

interface ApiError {
  code: string;
  message: string;
}

interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
}

let getAuthToken: (() => Promise<string>) | null = null;
let currentTenantId: string | null = null;

export function setAuthTokenProvider(provider: () => Promise<string>) {
  getAuthToken = provider;
}

export function setCurrentTenantId(tenantId: string | null) {
  currentTenantId = tenantId;
}

export async function api<T>(
  path: string,
  options: ApiOptions = {}
): Promise<ApiResponse<T>> {
  const { method = 'GET', body, headers = {} } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (getAuthToken) {
    const token = await getAuthToken();
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  if (currentTenantId) {
    requestHeaders['X-Tenant-Id'] = currentTenantId;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    return {
      error: errorBody?.error || {
        code: `HTTP_${response.status}`,
        message: response.statusText,
      },
    };
  }

  if (response.status === 204) {
    return { data: undefined as T };
  }

  const data = await response.json();
  return { data };
}

export const apiClient = {
  get: <T>(path: string) => api<T>(path),
  post: <T>(path: string, body?: unknown) =>
    api<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) =>
    api<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
};
